import { fileLoadingAtom } from '@/app/atoms/fileLoadingAtom';
import { ROUTE_LOADER_IDS } from '@/shared/constants/routes';
import { useAtomValue } from 'jotai';
import type { ApiTypes } from 'quadratic-shared/typesAndSchemas';
import { useRouteLoaderData } from 'react-router';

export type FileRouteLoaderData = ApiTypes['/v0/files/:uuid.GET.response'];

/**
 * Hook to get the current file data. Returns undefined in embed import mode
 * or when called outside of file/embed routes.
 *
 * Reads from `fileLoadingAtom` (the source of truth populated by `loadFile`)
 * so consumers automatically see updates like the read-only permission
 * downgrade applied while previewing a historical file version. Falls back to
 * the React Router loader data during brief transitions when the atom's
 * `data` is momentarily `null` (e.g. while switching versions).
 */
export const useFileRouteLoaderData = (): FileRouteLoaderData | undefined => {
  const routeFileData = useRouteLoaderData(ROUTE_LOADER_IDS.FILE) as FileRouteLoaderData | undefined;
  const atomFileData = useAtomValue(fileLoadingAtom).data;

  return atomFileData ?? routeFileData;
};

/**
 * Hook that requires file route loader data to exist. Use in components that
 * are guaranteed to only render in the file route context (not in embed mode).
 * Throws an error if data is not available.
 */
export const useFileRouteLoaderDataRequired = (): FileRouteLoaderData => {
  const data = useFileRouteLoaderData();
  if (!data) {
    throw new Error('useFileRouteLoaderDataRequired must be used in a file route context');
  }
  return data;
};
